import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  Download,
  Info,
  Loader2,
  Lock,
  RefreshCw,
  Wallet,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";
import { formatInr } from "./runPayrollUtils";
import { displayLabel, employeeDisplayName } from "../../../utils/displayLabel";
import { exportFnfSettlementExcel } from "./exportFnfSettlementExcel";
import { PAYROLL_ACTION_CHROME, Icon3dFnf } from "./payrollActionChrome";

function netFromComponents(components) {
  return (
    Math.round(
      (components || []).reduce((sum, c) => {
        if (!c || c.included === false) return sum;
        const amt = Number(c.finalAmount != null ? c.finalAmount : c.systemAmount) || 0;
        return c.type === "deduction" ? sum - amt : sum + amt;
      }, 0) * 100
    ) / 100
  );
}

function InfoTip({ text }) {
  if (!text) return null;
  return (
    <span
      title={text}
      className="inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center text-[hsl(var(--ds-muted-fg))]"
      aria-label={text}
    >
      <Info size={14} strokeWidth={1.75} className="h-3.5 w-3.5" />
    </span>
  );
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Full & Final settlement workspace — single modern panel (no duplicate wizard steps).
 * Shared by Actions Process F&F and Run Payroll Left Employees.
 */
export default function LeftEmployeesFnfModal({
  darkMode,
  payMonth,
  employee,
  onClose,
  onSaved,
  initialIncludeSalary = null,
  startAtReview = false,
  forceReadOnly = false,
  canMarkPaid = true,
}) {
  const [includeSalary, setIncludeSalary] = useState(!!initialIncludeSalary);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [components, setComponents] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [meta, setMeta] = useState(null);
  const [salaryGate, setSalaryGate] = useState(null);
  const [unpaidMonths, setUnpaidMonths] = useState([]);

  const border = PAYROLL_ACTION_CHROME.borderClass;
  const muted = "text-[hsl(var(--ds-muted-fg))]";
  const readOnly =
    forceReadOnly || ["paid", "hold"].includes(String(meta?.status || ""));

  const load = async ({ recalculate = false, include = includeSalary } = {}) => {
    if (!employee?.employeeId || !payMonth) return;
    if (recalculate) setRecalcBusy(true);
    else setLoading(true);
    try {
      const res = recalculate
        ? await payrollOpsService.recalculateFnfSettlement(employee.employeeId, {
            payMonth,
            includeLeavingSalary: include,
            salaryPayMonth: payMonth,
            bookingPayMonth: payMonth,
          })
        : await payrollOpsService.getFnfSettlement(employee.employeeId, payMonth, {
            includeLeavingSalary: include,
          });
      const data = res?.data || {};
      const comps = Array.isArray(data.componentsJson) ? data.componentsJson : [];
      const unpaid = Array.isArray(data.unpaidMonths) ? data.unpaidMonths : [];
      setComponents(comps);
      setRemarks(data.remarks || "");
      setMeta(data);
      setSalaryGate(data.salaryGate || null);
      setUnpaidMonths(unpaid);
      if (data.includeSalaryLocked) {
        setIncludeSalary(false);
      } else if (data.includeLeavingSalary != null) {
        setIncludeSalary(!!data.includeLeavingSalary);
      }

      // Backfill unpaid-month lines for settlements saved before unpaid ledger integration
      const locked = ["paid", "hold"].includes(String(data.status || ""));
      const hasUnpaidLines = comps.some((c) => c.section === "unpaid_salary");
      if (
        !recalculate &&
        !locked &&
        unpaid.length > 0 &&
        !hasUnpaidLines
      ) {
        setLoading(false);
        return load({
          recalculate: true,
          include:
            data.includeSalaryLocked
              ? false
              : data.includeLeavingSalary != null
                ? !!data.includeLeavingSalary
                : include,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load F&F");
    } finally {
      setLoading(false);
      setRecalcBusy(false);
    }
  };

  useEffect(() => {
    load({ recalculate: false, include: includeSalary });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.employeeId, payMonth]);

  const net = useMemo(() => netFromComponents(components), [components]);

  const unpaidLines = useMemo(
    () => (components || []).filter((c) => c.section === "unpaid_salary"),
    [components]
  );
  const salaryLines = useMemo(
    () => (components || []).filter((c) => c.section === "salary"),
    [components]
  );
  const settlementLines = useMemo(
    () =>
      (components || []).filter(
        (c) => c.section !== "salary" && c.section !== "unpaid_salary"
      ),
    [components]
  );

  const unpaidTotal = useMemo(
    () =>
      round2(
        (unpaidMonths || []).reduce((s, m) => s + (Number(m.unpaid) || 0), 0)
      ),
    [unpaidMonths]
  );

  const updateComponent = (key, patch) => {
    if (readOnly) return;
    setComponents((prev) =>
      prev.map((c) => {
        if (c.key !== key) return c;
        const next = { ...c, ...patch };
        if (patch.finalAmount != null) {
          next.overridden =
            Math.round(Number(patch.finalAmount) * 100) !==
            Math.round(Number(next.systemAmount || 0) * 100);
        }
        return next;
      })
    );
  };

  const handleIncludeSalaryToggle = async (checked) => {
    if (readOnly || salaryGate?.locked || meta?.includeSalaryLocked) return;
    setIncludeSalary(checked);
    await load({ recalculate: true, include: checked });
  };

  const handleSave = async (opts = {}) => {
    if (forceReadOnly) {
      toast.error("No permission to edit F&F. Grant Edit on Process F&F.");
      return;
    }
    if (opts.markPaid && !canMarkPaid) {
      toast.error("No permission to mark F&F paid. Grant Approve on Process F&F.");
      return;
    }
    setSaving(true);
    try {
      await payrollOpsService.updateFnfSettlement(employee.employeeId, {
        payMonth,
        components,
        remarks,
        includeLeavingSalary: includeSalary,
        status: opts.markPaid ? "paid" : "ready",
        markPaid: !!opts.markPaid,
      });
      toast.success(opts.markPaid ? "F&F marked paid" : "F&F saved");
      onSaved?.();
      if (!opts.keepOpen) onClose?.();
      else await load({ recalculate: false, include: includeSalary });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      await exportFnfSettlementExcel({
        employee,
        settlement: { ...meta, componentsJson: components },
        payMonth,
      });
    } catch (err) {
      toast.error(err?.message || "Could not export statement");
    }
  };

  if (typeof document === "undefined") return null;

  const salaryLocked = !!(salaryGate?.locked || meta?.includeSalaryLocked);
  const lockReason =
    salaryGate?.reason || meta?.includeSalaryLockReason || "Salary already processed";

  // startAtReview / initialIncludeSalary kept for call-site compatibility
  void startAtReview;

  return createPortal(
    <div className="fixed inset-0 z-[100050] flex items-stretch justify-center bg-[hsl(var(--ds-fg)/0.45)] p-3 sm:p-5">
      <div
        className={`flex w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-[hsl(var(--ds-bg))] shadow-[var(--ds-shadow-panel)] ${PAYROLL_ACTION_CHROME.cardBorderClass}`}
        role="dialog"
        aria-modal="true"
        aria-label="Full and Final settlement"
      >
        <div className={PAYROLL_ACTION_CHROME.stripeClass} />
        <div className="relative shrink-0 border-b border-[hsl(var(--ds-primary)/0.25)] bg-[hsl(var(--ds-surface))] px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-primary)/0.06)]">
                <Icon3dFnf />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--ds-primary))]">
                    Full &amp; Final
                  </span>
                  {readOnly ? (
                    <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                      <CheckCircle2 size={11} /> {displayLabel(meta?.status, "Paid")}
                    </span>
                  ) : (
                    <span className="rounded-md border border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-primary)/0.06)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-fg))]">
                      {displayLabel(meta?.processMethodLabel || meta?.processMethod, "Draft")}
                    </span>
                  )}
                </div>
                <h3 className="mt-1 truncate text-lg font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
                  {employeeDisplayName(employee)}
                </h3>
                <p className={`mt-0.5 text-xs ${muted}`}>
                  {[
                    employee?.empCode,
                    `Booking ${payMonth}`,
                    meta?.lastWorkingDay || employee?.lastWorkingDay
                      ? `LWD ${formatDate(meta?.lastWorkingDay || employee?.lastWorkingDay)}`
                      : null,
                    meta?.yearsOfService != null ? `${meta.yearsOfService} yrs service` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg border p-1.5 ${border} bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]`}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Kpi label="Net F&F" value={loading ? "…" : formatInr(net)} emphasize />
            <Kpi label="Unpaid months" value={loading ? "…" : String(unpaidMonths.length)} />
            <Kpi label="Open unpaid" value={loading ? "…" : formatInr(unpaidTotal)} />
            <Kpi
              label="Settlement heads"
              value={
                loading
                  ? "…"
                  : String(settlementLines.filter((c) => c.included !== false).length)
              }
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          {loading ? (
            <div className={`flex h-48 items-center justify-center gap-2 text-sm ${muted}`}>
              <Loader2 size={16} className="animate-spin" /> Building settlement…
            </div>
          ) : (
            <div className="space-y-5">
              {/* Scope */}
              <section className={`rounded-xl border ${border} bg-[hsl(var(--ds-surface))] p-4`}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--ds-fg))]">
                      Settlement scope
                    </h4>
                    <InfoTip text="Optionally include leaving-month salary in this F and F. Prior unpaid salary months from the payment ledger are listed below and included in net when checked. Settlement heads (encashment, gratuity, notice, recoveries) are always calculated from policy." />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={recalcBusy || readOnly}
                      onClick={() => load({ recalculate: true, include: includeSalary })}
                      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${border} hover:bg-[hsl(var(--ds-muted))] disabled:opacity-50`}
                    >
                      {recalcBusy ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <RefreshCw size={12} />
                      )}
                      Recalculate
                    </button>
                    <button
                      type="button"
                      onClick={handleExport}
                      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
                    >
                      <Download size={12} /> Statement
                    </button>
                  </div>
                </div>

                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 ${border} ${
                    salaryLocked ? "opacity-70" : "hover:bg-[hsl(var(--ds-muted)/0.45)]"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={includeSalary && !salaryLocked}
                    disabled={salaryLocked || readOnly || recalcBusy}
                    onChange={(e) => handleIncludeSalaryToggle(e.target.checked)}
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--ds-fg))]">
                      Include leaving-month salary
                      <InfoTip text="Turn on for advance exit (salary + settlement in one payment). Leave off when salary already runs in regular payroll. Locked when leaving-month salary is already paid or booked." />
                    </span>
                    {salaryLocked ? (
                      <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                        <Lock size={12} /> {lockReason}
                      </span>
                    ) : null}
                  </span>
                </label>
              </section>

              {/* Unpaid salary all months */}
              <section className={`rounded-xl border ${border} bg-[hsl(var(--ds-surface))] p-4`}>
                <div className="mb-3 flex items-center gap-1.5">
                  <Wallet size={14} className="text-[hsl(var(--ds-primary))]" />
                  <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--ds-fg))]">
                    Unpaid salary — all months
                  </h4>
                  <InfoTip text="Open salary obligations from the payment ledger across every pay month. Amounts with Include checked roll into Net F and F. Hold-only months stay informational until released." />
                </div>
                {unpaidMonths.length === 0 && unpaidLines.length === 0 ? (
                  <p
                    className={`rounded-lg border border-dashed px-3 py-6 text-center text-xs ${border} ${muted}`}
                  >
                    No open unpaid salary months for this employee.
                  </p>
                ) : (
                  <>
                    {unpaidMonths.length > 0 ? (
                      <div className={`mb-3 overflow-hidden rounded-lg border ${border}`}>
                        <table className="w-full text-xs">
                          <thead className="bg-[hsl(var(--ds-muted)/0.55)]">
                            <tr>
                              <th className="px-2.5 py-2 text-left font-semibold">Month</th>
                              <th className="px-2.5 py-2 text-right font-semibold">Net</th>
                              <th className="px-2.5 py-2 text-right font-semibold">Paid</th>
                              <th className="px-2.5 py-2 text-right font-semibold">Unpaid</th>
                              <th className="px-2.5 py-2 text-right font-semibold">Hold</th>
                              <th className="px-2.5 py-2 text-left font-semibold">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unpaidMonths.map((m) => (
                              <tr key={m.disbursementId || m.payMonth} className={`border-t ${border}`}>
                                <td className="px-2.5 py-2 font-semibold">{m.payMonth}</td>
                                <td className="px-2.5 py-2 text-right tabular-nums">
                                  {formatInr(m.netAmount)}
                                </td>
                                <td className="px-2.5 py-2 text-right tabular-nums">
                                  {formatInr(m.paidAmount)}
                                </td>
                                <td className="px-2.5 py-2 text-right tabular-nums font-semibold text-[hsl(var(--ds-primary))]">
                                  {formatInr(m.unpaid)}
                                </td>
                                <td className="px-2.5 py-2 text-right tabular-nums">
                                  {formatInr(m.hold)}
                                </td>
                                <td className="px-2.5 py-2 capitalize">{displayLabel(m.status)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    {unpaidLines.length > 0 ? (
                      <ComponentTable
                        rows={unpaidLines}
                        readOnly={readOnly}
                        border={border}
                        updateComponent={updateComponent}
                      />
                    ) : null}
                  </>
                )}
              </section>

              {salaryLines.some(
                (c) => c.key === "leaving_month_salary_net" && c.included !== false
              ) ? (
                <section className={`rounded-xl border ${border} bg-[hsl(var(--ds-surface))] p-4`}>
                  <div className="mb-3 flex items-center gap-1.5">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--ds-fg))]">
                      Leaving-month salary
                    </h4>
                    <InfoTip text="Net leaving-month salary from the Process Payroll engine (attendance, variables, statutory, TDS)." />
                  </div>
                  <ComponentTable
                    rows={salaryLines.filter(
                      (c) => c.key === "leaving_month_salary_net" || c.included !== false
                    )}
                    readOnly={readOnly}
                    border={border}
                    updateComponent={updateComponent}
                    showOnlyNet
                  />
                </section>
              ) : null}

              <section className={`rounded-xl border ${border} bg-[hsl(var(--ds-surface))] p-4`}>
                <div className="mb-3 flex items-center gap-1.5">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--ds-fg))]">
                    Settlement heads
                  </h4>
                  <InfoTip text="Policy-driven heads: leave encashment, gratuity, notice, LOP recovery, loan/advance/asset recovery, reimbursements. Override Final only when HR confirms." />
                </div>
                <ComponentTable
                  rows={settlementLines}
                  readOnly={readOnly}
                  border={border}
                  updateComponent={updateComponent}
                />
              </section>

              <section>
                <label className={`mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase ${muted}`}>
                  Remarks
                  <InfoTip text="Optional overall notes stored with the settlement for audit." />
                </label>
                <textarea
                  value={remarks}
                  disabled={readOnly}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={2}
                  className={`w-full rounded-xl border px-3 py-2.5 text-xs ${border} bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] disabled:opacity-60`}
                  placeholder="Optional overall remarks"
                />
              </section>
            </div>
          )}
        </div>

        <div
          className={`sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3.5 sm:px-6 ${border} bg-[hsl(var(--ds-surface))]`}
        >
          <div className="rounded-xl border border-[hsl(var(--ds-primary)/0.28)] bg-[hsl(var(--ds-primary)/0.07)] px-4 py-2.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--ds-muted-fg))]">
              Net payable
            </span>
            <div className="text-lg font-bold tabular-nums text-[hsl(var(--ds-fg))]">
              {formatInr(net)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg border px-3.5 py-2 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly ? (
              <>
                <button
                  type="button"
                  disabled={loading || saving}
                  onClick={() => handleSave({ markPaid: false })}
                  className={`rounded-lg border px-3.5 py-2 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))] disabled:opacity-50`}
                >
                  {saving ? <Loader2 size={12} className="inline animate-spin" /> : null} Save
                </button>
                {canMarkPaid ? (
                  <button
                    type="button"
                    disabled={loading || saving}
                    onClick={() => handleSave({ markPaid: true })}
                    className="rounded-lg bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-50"
                  >
                    Save &amp; mark paid
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function Kpi({ label, value, emphasize = false }) {
  return (
    <div
      className={`rounded-xl border border-[hsl(var(--ds-primary)/0.3)] bg-[hsl(var(--ds-primary)/0.05)] px-3 py-2 ${
        emphasize ? "ring-1 ring-[hsl(var(--ds-primary)/0.35)]" : ""
      }`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
        {label}
      </div>
      <div
        className={`mt-0.5 truncate text-sm font-bold tabular-nums ${
          emphasize ? "text-[hsl(var(--ds-primary))]" : "text-[hsl(var(--ds-fg))]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function ComponentTable({ rows, readOnly, border, updateComponent, showOnlyNet = false }) {
  const list = showOnlyNet
    ? (rows || []).filter((c) => c.key === "leaving_month_salary_net")
    : rows || [];
  if (!list.length) {
    return (
      <p className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-[hsl(var(--ds-muted-fg))]">
        No lines
      </p>
    );
  }
  return (
    <div className={`overflow-hidden rounded-xl border ${border}`}>
      <table className="w-full text-xs">
        <thead className="bg-[hsl(var(--ds-muted)/0.55)] text-[hsl(var(--ds-fg))]">
          <tr>
            <th className="px-2.5 py-2 text-left font-semibold">Include</th>
            <th className="px-2.5 py-2 text-left font-semibold">Component</th>
            <th className="px-2.5 py-2 text-right font-semibold">System</th>
            <th className="px-2.5 py-2 text-right font-semibold">Final</th>
            <th className="px-2.5 py-2 text-left font-semibold">Remarks</th>
          </tr>
        </thead>
        <tbody>
          {list.map((c) => {
            const isDed = c.type === "deduction";
            return (
              <tr key={c.key} className={`border-t ${border}`}>
                <td className="px-2.5 py-2">
                  <input
                    type="checkbox"
                    disabled={readOnly || c.locked}
                    checked={c.included !== false}
                    onChange={(e) => updateComponent(c.key, { included: e.target.checked })}
                  />
                </td>
                <td className="px-2.5 py-2">
                  <div className="font-semibold text-[hsl(var(--ds-fg))]">{c.label}</div>
                  <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
                    {isDed ? "Deduction" : "Earning"}
                    {c.overridden ? " · overridden" : ""}
                  </div>
                </td>
                <td
                  className={`px-2.5 py-2 text-right tabular-nums ${
                    isDed ? "text-red-600" : "text-[hsl(var(--ds-fg))]"
                  }`}
                >
                  {formatInr(c.systemAmount)}
                </td>
                <td className="px-2.5 py-2 text-right">
                  <input
                    type="number"
                    step="0.01"
                    disabled={readOnly || c.locked}
                    value={c.finalAmount ?? c.systemAmount ?? 0}
                    onChange={(e) =>
                      updateComponent(c.key, {
                        finalAmount: Number(e.target.value),
                        overridden: true,
                      })
                    }
                    className={`w-28 rounded-md border px-2 py-1 text-right tabular-nums ${border} bg-[hsl(var(--ds-bg))] ${
                      isDed ? "text-red-600" : ""
                    } disabled:opacity-60`}
                  />
                </td>
                <td className="px-2.5 py-2">
                  <input
                    type="text"
                    disabled={readOnly}
                    value={c.remarks || ""}
                    onChange={(e) => updateComponent(c.key, { remarks: e.target.value })}
                    placeholder="—"
                    className={`w-full min-w-[8rem] rounded-md border px-2 py-1 ${border} bg-[hsl(var(--ds-bg))] disabled:opacity-60`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
